import { getLocalTimeZone } from "@internationalized/date";
import { TrainIdentifier } from "@vygruppen/vy-train-map";
import {
  BatchTrainForm,
  GroupTrainForm,
  ProductionDeviationForm,
} from "features/CenterContent/RoleContent/ProductionDeviation/types/formSchema";
import { TrainListRange } from "features/CenterContent/RoleContent/ProductionDeviation/useTrainListRange";
import { TimedFormDuration } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import { useProductionDeviations } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/UpcomingEventsModal/useProductionDeviation";
import { OperationalInformationTypes } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import { TrainInfoBatchFormProps } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoBatchForm";
import { TrainFormGroup } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { TrainInformationDetailedResponse } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/responseSchema";
import {
  baseDefaultFormSchema,
  trainDetailedResponseToForm,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/transformers";
import { toTrainSerie } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/utils";
import {
  getDirection,
  getDirectionText,
} from "features/VehicleSidebar/AffectedTrains/utils/utils";
import { groupBy } from "features/VehicleSidebar/VehicleList/TrainList/TrainList";
import { TrainData } from "features/VehicleSidebar/VehicleList/TrainList/types";
import { useEffect, useRef, useState } from "react";
import {
  DefaultValues,
  FieldPath,
  useFormContext,
  UseFormReturn,
  useWatch,
} from "react-hook-form";
import { partition } from "shared/utils/arrayUtils";
import { calendarDateTimeToDate } from "shared/utils/dateUtils";
import { css } from "styled-components";

const toTrainId = ({ trainNumber, trainDate, countryCode }: TrainData) => ({
  identifier: trainNumber,
  nominalDate: trainDate,
  countryCode,
});

const groupIdsBySeries = (trainIds: TrainIdentifier[]) =>
  groupBy(trainIds, ({ identifier }) => toTrainSerie(identifier));

export const groupTrainsBySeries = (trainList: TrainData[]) =>
  groupBy(trainList, (train) => toTrainSerie(train.trainNumber));

export const sortTrainSeries = (trainSeries: string[]) =>
  trainSeries.toSorted((a, b) => {
    const asNumber = (ts: string) => parseInt(ts.replace("x", "0"), 10);
    return asNumber(a) - asNumber(b);
  });

const productionDeviationGroupPath = (trainSerie: string) =>
  `productionDeviationForm.${trainSerie}` as const;

const productionDeviationPath = (trainSerie: string) => ({
  path: <T extends FieldPath<GroupTrainForm>>(field: T) =>
    `${productionDeviationGroupPath(trainSerie)}.${field}` as const,
});

export const useProductionDeviationPath = productionDeviationPath;
export type Path = ReturnType<typeof useProductionDeviationPath>["path"];

export const getAllRepresentativeTrains = (trainIds: TrainIdentifier[]) => {
  const groupedData = groupIdsBySeries(trainIds);
  const representativeTrainIds = Object.values(groupedData)
    .map((group) =>
      partition(group, (it) => getDirection(it) === "ODD").map((it) => it[0]),
    )
    .flat()
    .filter(Boolean);
  return representativeTrainIds;
};

export const filterDurations = (
  trainList: TrainData[],
  durations: TimedFormDuration,
) => {
  const startDateTime = durations.from_time.toDate(getLocalTimeZone());
  const endDateTime = durations.to_time.toDate(getLocalTimeZone());

  if (startDateTime >= endDateTime) {
    return [];
  }

  const trainListFiltered = trainList.filter(
    (it) =>
      it.destinationScheduledArrivalTime &&
      it.destinationScheduledArrivalTime > startDateTime &&
      it.originScheduledDepartureTime &&
      it.originScheduledDepartureTime < endDateTime,
  );
  const trainIdsFiltered = trainListFiltered.map(toTrainId);
  return trainIdsFiltered.map((it) => it.identifier);
};

/**
 * Recreation of createInitialBatchFormStateFromTrainSeries but for ProductionDeviation
 */
export const createInitialBatchFormState = (
  trainSeries: string,
): DefaultValues<TrainFormGroup>[] => {
  const directions = ["ODD", "EVEN"] as const;
  return directions.map((direction) => ({
    trainSeries,
    groupLabel: trainSeries,
    route: "ROUTE",
    direction,
    trainIdsInGroup: [],
    trainForm: baseDefaultFormSchema(),
  }));
};

// Seeds a newly added train series with the shared event/reason/action instead of resetting to defaults.
export const seedBatchForSeries = (
  trainSeries: string,
  sharedBatchTrainForm?: DefaultValues<TrainFormGroup>[],
): DefaultValues<TrainFormGroup>[] => {
  const base = createInitialBatchFormState(trainSeries);
  if (!sharedBatchTrainForm) return base;
  return base.map((group, i) => ({
    ...group,
    trainForm: structuredClone(
      sharedBatchTrainForm[i]?.trainForm ?? group.trainForm,
    ),
  }));
};

export const createBatchFormStateFromResponse = (
  trainSeries: string,
  trainEvents: TrainInformationDetailedResponse[],
): DefaultValues<TrainFormGroup>[] => {
  const groups = groupBy(trainEvents, (it) => getDirection(it.trainIdentifier));
  const directions = ["ODD", "EVEN"] as const;
  return directions.map((direction, i) => {
    const trainEventsGroup = groups[direction] ?? [];
    const otherDirection = directions[1 - i];
    const trainEventsOtherGroup = groups[otherDirection] ?? [];
    const representativeTrainEvent =
      trainEventsGroup[0] ?? trainEventsOtherGroup[0]; // We prefer a train from this group, but pick from the other group if necessary
    return {
      trainSeries,
      groupLabel: trainSeries,
      route: "ROUTE",
      direction,
      trainIdsInGroup: trainEventsGroup.map((it) => it.trainIdentifier),
      trainForm: representativeTrainEvent
        ? trainDetailedResponseToForm(representativeTrainEvent)
        : baseDefaultFormSchema(),
    };
  });
};

export const makeBatchProps = (
  path: Path,
  opInfoTypes: OperationalInformationTypes,
  mode: "create" | "edit",
): TrainInfoBatchFormProps => ({
  infrastructureEvent: undefined,
  groupsInSeries: 2,
  activeTrainSeriesKey: path("batchTrainForm") as `\${groupKey}`,
  opInfoTypes,
  mode,
  lockEventType: mode === "edit",
});

/**
 * Done like this since we need to update the batchTrainForm at the same time as trainSeries is set
 */
export const useTrainSeries = () => {
  const { getValues, setValue, unregister } =
    useFormContext<ProductionDeviationForm>();
  const [trainSeries, setTrainSeries] = useState<string[]>(() =>
    Object.keys(getValues("productionDeviationForm") ?? {}),
  );
  const prevTrainSeries = useRef<string[]>(trainSeries);

  // Keep a template draft for Hendelse/Årsak/Handling before any train serie is picked.
  useEffect(() => {
    if (getValues("templateBatchTrainForm")) return;
    setValue(
      "templateBatchTrainForm",
      createInitialBatchFormState("template") as unknown as BatchTrainForm,
    );
  }, []);

  useEffect(() => {
    // Without this check a single refresh from editing code will reset the form, which is hard to work with
    if (
      trainSeries.length !== 0 &&
      JSON.stringify(trainSeries) === JSON.stringify(prevTrainSeries.current)
    ) {
      return;
    }
    const prevTrainSeriesSnapshot = prevTrainSeries.current;
    prevTrainSeries.current = [...trainSeries];

    // Sync the form record without a full form reset.
    const current = getValues("productionDeviationForm") ?? {};
    const existingSeries = Object.keys(current);
    const sharedBatchTrainForm =
      getValues("templateBatchTrainForm") ??
      (existingSeries.length
        ? current[existingSeries[0]].batchTrainForm
        : undefined);

    existingSeries
      .filter((series) => !trainSeries.includes(series))
      .forEach((series) => unregister(`productionDeviationForm.${series}`));

    // Seed series newly added since the previous sync.
    const newlyAddedSeries = trainSeries.filter(
      (series) => !prevTrainSeriesSnapshot.includes(series),
    );
    newlyAddedSeries.forEach((series) =>
      setValue(`productionDeviationForm.${series}`, {
        selectedTrains: [],
        batchTrainForm: seedBatchForSeries(series, sharedBatchTrainForm),
      } as unknown as GroupTrainForm),
    );
  }, [trainSeries]);

  return [trainSeries, setTrainSeries] as const;
};

// Create mode: propagate the shared templateBatchTrainForm to every selected serie;
// Edit mode:   there's only one serie, so path is returned as-is.
export const useProductionDeviationEffectivePath = (
  path: Path,
  isEditMode: boolean,
  trainSeries: string[],
) => {
  const { control, setValue } = useFormContext<ProductionDeviationForm>();
  const templatePath = ((_field: "batchTrainForm") =>
    "templateBatchTrainForm" as const) as unknown as Path;
  const effectivePath = isEditMode ? path : templatePath;

  const batchTrainForm = useWatch({
    name: effectivePath("batchTrainForm"),
    control,
  });

  useEffect(() => {
    if (!batchTrainForm) return;
    const targetTrainSeries = isEditMode ? trainSeries.slice(1) : trainSeries;
    targetTrainSeries.forEach((trainSerie) => {
      const { path: otherTrainSeriePath } =
        useProductionDeviationPath(trainSerie);
      const directionsIndexes = [0, 1];
      directionsIndexes.forEach((i) => {
        const field = otherTrainSeriePath(`batchTrainForm.${i}.trainForm`);
        setValue(field, batchTrainForm[i].trainForm);
      });
    });
  }, [batchTrainForm]);

  return effectivePath;
};

/**
 * Done like this since we need to update the batchTrainForm at the same time as selectedTrains is set
 */
export const useSelectedTrains = (
  trainSerie: string,
  trainList: TrainListRange["trainList"],
) => {
  const { path } = useProductionDeviationPath(trainSerie);
  const { setValue, watch } = useFormContext<ProductionDeviationForm>();

  const selectedTrains = watch(path("selectedTrains")) ?? [];
  const [_selectedTrains, setSelectedTrains] =
    useState<string[]>(selectedTrains);
  useEffect(() => {
    setValue(path("selectedTrains"), _selectedTrains);

    // Select trains by identifier and add countryCode and nominalDate at the end.
    // An empty trainList means no trains run in the selected period.
    const anyTrain = trainList[0];
    if (!anyTrain) return;
    const { countryCode, trainDate: nominalDate } = anyTrain;

    ["ODD", "EVEN"].forEach((dir, index) =>
      setValue(
        path(`batchTrainForm.${index}.trainIdsInGroup`),
        _selectedTrains
          .filter((identifier) => getDirectionText(identifier) === dir)
          .map(
            (identifier: string): TrainIdentifier => ({
              identifier,
              nominalDate,
              countryCode,
            }),
          ),
      ),
    );
  }, [_selectedTrains]);
  return [selectedTrains, setSelectedTrains] as const;
};

export const PANEL_STYLE = css`
  background-color: ${({ theme }) => theme.colorBackgroundSecondary};
  padding: 18px;
  border-radius: 18px;
`;

export const triggerDurationsTypeAndReason = async (
  formContext: UseFormReturn<ProductionDeviationForm>,
  path: Path,
  isEditMode: boolean,
) => {
  const { getFieldState, trigger } = formContext;
  await trigger();
  // All fields except these three get set in the next modal step
  // Durations can't be edited in edit mode, so its date-range validity is irrelevant there
  const durations = getFieldState("durations");
  const type = getFieldState(path(`batchTrainForm.0.trainForm.type`));
  const reason = getFieldState(path(`batchTrainForm.0.trainForm.reason`));
  return !type.error && !reason.error && (isEditMode || !durations.error);
};

export const triggerSkipEmptyDirections = async (
  formContext: UseFormReturn<ProductionDeviationForm>,
  path: Path,
) => {
  const { watch, getFieldState, trigger } = formContext;

  await trigger();
  const batchTrainForm = watch(path(`batchTrainForm`));

  return batchTrainForm.every((form, index) => {
    if (form.trainIdsInGroup.length === 0) return true;

    const fieldState = getFieldState(path(`batchTrainForm.${index}`));
    return !fieldState.error;
  });
};

export const submitSkipEmptyDirections = async (
  formContext: UseFormReturn<ProductionDeviationForm>,
  onSubmit: () => Promise<void>,
) => {
  const { getValues, setValue, trigger } = formContext;

  const currentValues = getValues();

  const batchTrainForms = currentValues.productionDeviationForm;
  Object.keys(batchTrainForms).forEach((trainSerie) => {
    const batchTrainForm = batchTrainForms[trainSerie].batchTrainForm.filter(
      (it) => it.trainIdsInGroup.length > 0,
    );
    setValue(
      `productionDeviationForm.${trainSerie}.batchTrainForm`,
      batchTrainForm,
    );
  });

  // reset templateBatchTrainForm before submitting as it's UI-only.
  setValue("templateBatchTrainForm", undefined);

  await trigger();

  void onSubmit();
};

export const substitudePreviewText = (previewText: string, trainId: string) => {
  const substitution = "[Gjeldende tog]";
  return previewText
    .replaceAll(`Tog ${trainId}`, substitution)
    .replaceAll(trainId, substitution);
};

/**
 * We retrieve every productionDeviation (even those outside of this train serie).
 * Thus instead of making e.g. a new endpoint for this, just check through these metadata.
 */
export const useTrainsAlreadyInProductionDeviation = (
  trainSerie: string,
  durations: TimedFormDuration,
) => {
  const productionDeviations = useProductionDeviations();
  if (!productionDeviations.data) return [];
  const productionDeviationThisSerie = productionDeviations.data.filter(
    (it) =>
      it.trainIds.length > 0 && toTrainSerie(it.trainIds[0]) === trainSerie,
  );
  const productionDeviationThisPeriod = productionDeviationThisSerie.filter(
    (it) =>
      it.fromDate.toDate().getTime() <=
        calendarDateTimeToDate(durations.to_time).getTime() &&
      it.toDate.toDate().getTime() >=
        calendarDateTimeToDate(durations.from_time).getTime(),
  );
  return productionDeviationThisPeriod?.flatMap((it) => it.trainIds);
};
